import { applyLead, applyPlay, EMPTY_CHAIN } from './Chain.js';
import { dealHands } from './Deal.js';
import { hasLegalPlay } from './MoveValidator.js';
import { getScoringPolicy, winnerLeader } from './scoring/index.js';
import {
  isDouble,
  teamMapFor,
  tileEquals,
  tilePips,
  type Chain,
  type LastAction,
  type TeamMap,
  type Tile,
} from './types.js';
import type { DominoRuleSet, PlayerCount } from './variants/types.js';

export { matchWinnerSeats } from './scoring/index.js';

/**
 * Pending Muggins window. In strict mode, a play whose open-ends sum is a
 * multiple of 5 produces a pendingScore: the scoring player has until
 * `expiresAt` (ms epoch) to claim. Any other seat that claims first within
 * the window steals the points (Muggins rule).
 */
export interface PendingMuggins {
  readonly points: number;
  readonly scorerSeat: number;
  readonly expiresAt: number;
}

export interface HandResultRecord {
  readonly handNumber: number;
  readonly winnerSeats: readonly number[];
  readonly blocked: boolean;
  readonly perSeatAward: readonly number[];
  /** «Яйца» (penalty scoring): tied рыба, nothing recorded. Absent on legacy records. */
  readonly eggs?: boolean;
  /** «Лысый» finish (penalty scoring): this hand ended the match. Absent on legacy records. */
  readonly baldFinish?: boolean;
}

export interface GameStateData {
  readonly ruleSet: DominoRuleSet;
  readonly playerCount: PlayerCount;
  readonly seed: number;
  readonly handNumber: number;
  readonly hands: readonly (readonly Tile[])[];
  readonly boneyard: readonly Tile[];
  readonly chain: Chain;
  readonly currentSeat: number;
  readonly leaderSeat: number;
  readonly scores: readonly number[];
  readonly teamMap: TeamMap;
  readonly pendingMuggins: PendingMuggins | null;
  readonly handOver: boolean;
  readonly matchOver: boolean;
  readonly lastResult: HandResultRecord | null;
  /** Consecutive pass count — when it equals playerCount the hand is blocked. */
  readonly consecutivePasses: number;
  /**
   * Tile the leader MUST play as the lead. Set on first-hand creation when
   * any tile in `ruleSet.firstHandLeadPriority` lands in a player's hand (or the
   * lowest-pip fallback otherwise). Cleared the moment the lead is played.
   * Null for subsequent hands (winner of previous hand may lead with anything).
   */
  readonly forcedLeadTile: Tile | null;
  /**
   * Most recent in-hand engine action — used by the UI to render a
   * "last move + whose turn it is" banner. Cleared on hand boundaries
   * (next-hand reset). Carries through `STATE_SYNC` snapshots and replay.
   */
  readonly lastAction: LastAction | null;
  /** Penalty scoring: points carried from «яйца» hands to the next decided hand's loser. */
  readonly carriedPenalty: number;
}

/**
 * First-hand opening rule (`ruleSet.firstHandLeadPriority`). Searches every
 * seat's hand for the earliest tile in `priority`; the first match wins.
 * If none of the listed tiles are dealt (all in boneyard), falls
 * back to the lowest pip-sum tile in any hand (ties broken by lower
 * individual half pip, then lower seat index). The returned `forcedTile`
 * is what the leader MUST play on turn 1.
 */
export interface FirstHandLead {
  readonly seat: number;
  readonly forcedTile: Tile;
}

export function pickFirstHandLead(
  hands: readonly (readonly Tile[])[],
  priority: readonly Tile[],
): FirstHandLead {
  for (const wanted of priority) {
    for (let seat = 0; seat < hands.length; seat += 1) {
      if (hands[seat]!.some((t) => tileEquals(t, wanted))) {
        return { seat, forcedTile: wanted };
      }
    }
  }
  // Fallback: lowest tile by pip-sum (then by halves) across all hands.
  let bestSeat = 0;
  let bestTile: Tile | null = null;
  for (let seat = 0; seat < hands.length; seat += 1) {
    for (const t of hands[seat]!) {
      if (bestTile === null || tileRank(t) < tileRank(bestTile)) {
        bestSeat = seat;
        bestTile = t;
      }
    }
  }
  if (bestTile === null) {
    // Should not happen — at least one seat must have at least one tile.
    throw new Error('pickFirstHandLead: no tiles dealt to any seat');
  }
  return { seat: bestSeat, forcedTile: bestTile };
}

/** Comparable rank used by the fallback lowest-tile selector. */
function tileRank(t: Tile): number {
  return tilePips(t) * 100 + t[0] * 10 + t[1];
}

export function createInitialState(
  ruleSet: DominoRuleSet,
  playerCount: PlayerCount,
  seed: number,
): GameStateData {
  const deal = dealHands(ruleSet, playerCount, seed);
  const teamMap = teamMapFor(playerCount, ruleSet.partnership);
  const priority = ruleSet.firstHandLeadPriority;
  const leaderInfo = priority !== null
    ? pickFirstHandLead(deal.hands, priority)
    : { seat: pickHeaviestLeader(deal.hands), forcedTile: null as Tile | null };
  return {
    ruleSet,
    playerCount,
    seed,
    handNumber: 1,
    hands: deal.hands,
    boneyard: deal.boneyard,
    chain: EMPTY_CHAIN,
    currentSeat: leaderInfo.seat,
    leaderSeat: leaderInfo.seat,
    scores: new Array<number>(playerCount).fill(0),
    teamMap,
    pendingMuggins: null,
    handOver: false,
    matchOver: false,
    lastResult: null,
    consecutivePasses: 0,
    forcedLeadTile: leaderInfo.forcedTile,
    lastAction: null,
    carriedPenalty: 0,
  };
}

/**
 * Standard All Fives opening: heaviest double leads; if no doubles, heaviest
 * non-double tile. Ties broken by lowest seat index. Used when the rule set
 * does not force a specific first-hand lead tile.
 */
export function pickHeaviestLeader(hands: readonly (readonly Tile[])[]): number {
  let bestSeat = 0;
  let bestRank = -1;
  for (let seat = 0; seat < hands.length; seat += 1) {
    for (const tile of hands[seat]!) {
      const isDbl = isDouble(tile);
      const rank = isDbl ? 1000 + tile[0] : tilePips(tile);
      if (rank > bestRank) {
        bestRank = rank;
        bestSeat = seat;
      }
    }
  }
  return bestSeat;
}

function withHandRemoved(hands: readonly (readonly Tile[])[], seat: number, tile: Tile): Tile[][] {
  return hands.map((hand, i) => {
    if (i !== seat) return hand.slice();
    return hand.filter((t) => !tileEquals(t, tile));
  });
}

function advanceSeat(state: GameStateData): number {
  return (state.currentSeat + 1) % state.playerCount;
}

/** Apply a play and produce the next state (does not advance turn — caller checks for hand-over first). */
export interface ApplyMoveResult {
  readonly state: GameStateData;
  /** Points scored by this play (open-ends sum if multiple of N, else 0). */
  readonly pointsThisPlay: number;
}

export function applyMove(
  state: GameStateData,
  seat: number,
  tile: Tile,
  armIndex: number,
  now: number,
): ApplyMoveResult {
  if (state.handOver) throw new Error('applyMove: hand is over');
  if (state.matchOver) throw new Error('applyMove: match is over');
  if (state.currentSeat !== seat) {
    throw new Error(`applyMove: seat ${seat} is not the current player (current = ${state.currentSeat})`);
  }
  const hand = state.hands[seat];
  if (!hand || !hand.some((t) => tileEquals(t, tile))) {
    throw new Error('applyMove: tile not in hand');
  }

  const isLead = state.chain.lead === null;
  if (isLead && state.forcedLeadTile && !tileEquals(tile, state.forcedLeadTile)) {
    throw new Error(
      `applyMove: lead is forced to [${state.forcedLeadTile[0]},${state.forcedLeadTile[1]}]`,
    );
  }

  const result = isLead
    ? applyLead(state.chain, tile)
    : applyPlay(state.chain, armIndex, tile, state.ruleSet.spinner);

  const newHands = withHandRemoved(state.hands, seat, tile);
  const points = getScoringPolicy(state.ruleSet).onPlay({
    ruleSet: state.ruleSet,
    openEndsSum: result.openEndsSum,
    tile,
    isLead,
  });
  const wentOut = newHands[seat]!.length === 0;

  // In lenient mode (or zero score), award directly. In strict mode with a
  // non-zero score, open a Muggins claim window — caller settles by calling
  // `claimMuggins` or `expireMuggins`.
  const nextScores = state.scores.slice();
  let pendingMuggins: PendingMuggins | null = state.pendingMuggins;
  if (points > 0) {
    if (state.ruleSet.mugginsStrict) {
      pendingMuggins = {
        points,
        scorerSeat: seat,
        expiresAt: now + state.ruleSet.mugginsClaimWindowMs,
      };
    } else {
      nextScores[seat] = (nextScores[seat] ?? 0) + points;
    }
  }

  let nextState: GameStateData = {
    ...state,
    hands: newHands,
    chain: result.chain,
    scores: nextScores,
    pendingMuggins,
    consecutivePasses: 0,
    // Once the lead has been played, the forced-lead constraint is satisfied.
    forcedLeadTile: isLead ? null : state.forcedLeadTile,
    lastAction: { kind: 'play', seat, tile, points, wentOut },
  };

  // Hand-over checks: player out OR boneyard empty AND nobody can play.
  if (wentOut) {
    nextState = finalizeHand(nextState, seat);
  } else {
    nextState = { ...nextState, currentSeat: advanceSeat(nextState) };
  }
  return { state: nextState, pointsThisPlay: points };
}

/** Draw one tile from the boneyard (or no-op if empty). Does not advance turn. */
export function applyDraw(state: GameStateData, seat: number): GameStateData {
  if (state.handOver) throw new Error('applyDraw: hand is over');
  if (state.currentSeat !== seat) throw new Error('applyDraw: not your turn');
  if (state.boneyard.length === 0) return state;
  const drawn = state.boneyard[0]!;
  const nextBoneyard = state.boneyard.slice(1);
  const nextHands = state.hands.map((hand, i) => (i === seat ? [...hand, drawn] : hand));
  return {
    ...state,
    hands: nextHands,
    boneyard: nextBoneyard,
    lastAction: { kind: 'draw', seat },
  };
}

/**
 * True when the hand is genuinely blocked: nobody can draw and no seat has
 * any legal play against the current chain. Gates the end-of-hand trigger
 * in `applyPass` so the hand can only end while a placeable tile remains
 * unreachable for every seat — not just because some pass counter rolled
 * over. Keeps `consecutivePasses` intact for AI inference (see
 * `PassConstraints` / `BeliefTracker`), which still relies on counting
 * recent passes against the current open pips.
 */
function isHandBlocked(state: GameStateData): boolean {
  if (state.boneyard.length > 0 && state.ruleSet.drawRule !== 'no-draw') return false;
  for (let seat = 0; seat < state.playerCount; seat += 1) {
    const hand = state.hands[seat] ?? [];
    if (hasLegalPlay(state.chain, hand, state.forcedLeadTile)) return false;
  }
  return true;
}

/** Pass turn. Legal only if the player has no legal play and (per drawRule) cannot/should not draw. */
export function applyPass(state: GameStateData, seat: number): GameStateData {
  if (state.handOver) throw new Error('applyPass: hand is over');
  if (state.currentSeat !== seat) throw new Error('applyPass: not your turn');
  const passes = state.consecutivePasses + 1;
  const afterPass: GameStateData = {
    ...state,
    consecutivePasses: passes,
    lastAction: { kind: 'pass', seat },
  };
  if (isHandBlocked(afterPass)) {
    return finalizeHand(afterPass, null);
  }
  return { ...afterPass, currentSeat: advanceSeat(afterPass) };
}

/** Claim the pending muggins by `seat`. In strict mode, any seat (not just the scorer) can claim. */
export function claimMuggins(state: GameStateData, seat: number, now: number): GameStateData {
  const pending = state.pendingMuggins;
  if (pending === null) return state;
  if (now > pending.expiresAt) return expireMuggins(state, now);
  const nextScores = state.scores.slice();
  nextScores[seat] = (nextScores[seat] ?? 0) + pending.points;
  // Carry the triggering play's tile through to the claim so the banner can
  // surface it ("P1 claimed +10 with [5|5]"). The prior engine action inside
  // an open Muggins window is always the play that opened the window.
  const triggerTile =
    state.lastAction !== null && state.lastAction.kind === 'play'
      ? state.lastAction.tile
      : undefined;
  let next: GameStateData = {
    ...state,
    scores: nextScores,
    pendingMuggins: null,
    lastAction: triggerTile !== undefined
      ? { kind: 'claim-muggins', seat, points: pending.points, tile: triggerTile }
      : { kind: 'claim-muggins', seat, points: pending.points },
  };
  if (getScoringPolicy(state.ruleSet).isMatchOver(nextScores, state.ruleSet)) {
    next = { ...next, matchOver: true, handOver: true };
  }
  return next;
}

/** Expire the pending muggins. The scorer keeps their points iff they claimed in time; if not they forfeit. */
export function expireMuggins(state: GameStateData, _now: number): GameStateData {
  const pending = state.pendingMuggins;
  if (pending === null) return state;
  // Strict mode: forfeit if the window expired without claim. The scorer
  // does not auto-receive — the points are lost. This is the "Muggins"
  // forfeiture rule. Callers who want lenient behavior should turn
  // mugginsStrict off in the ruleset.
  return { ...state, pendingMuggins: null };
}

function finalizeHand(state: GameStateData, wentOutSeat: number | null): GameStateData {
  const policy = getScoringPolicy(state.ruleSet);
  const eoh = policy.onHandEnd({
    hands: state.hands,
    teamMap: state.teamMap,
    wentOutSeat,
    ruleSet: state.ruleSet,
    scores: state.scores,
    carriedPenalty: state.carriedPenalty,
    lastPlayedTile: state.lastAction?.kind === 'play' ? state.lastAction.tile : null,
  });
  const nextScores = state.scores.map((score, seat) => score + (eoh.awarded[seat] ?? 0));
  const matchOver = eoh.baldFinish || policy.isMatchOver(nextScores, state.ruleSet);
  const result: HandResultRecord = {
    handNumber: state.handNumber,
    winnerSeats: eoh.winnerSeats,
    blocked: eoh.blocked,
    perSeatAward: eoh.awarded,
    eggs: eoh.eggs,
    baldFinish: eoh.baldFinish,
  };
  return {
    ...state,
    scores: nextScores,
    carriedPenalty: eoh.carriedPenalty,
    handOver: true,
    matchOver,
    lastResult: result,
  };
}

/**
 * Start the next hand of an ongoing match. Reshuffles, redeals; the leader
 * pick depends on the variant flag `nextHandLeader`:
 *  - 'winner' (All Fives, Козёл): winner of the previous hand leads; after
 *    a winnerless «яйца» hand, the seat that played the last tile.
 *  - 'rotate' (Телефон): the lead rotates clockwise — the seat to the left of
 *    the previous leader leads, regardless of who won.
 */
export function startNextHand(state: GameStateData, seed: number): GameStateData {
  if (!state.handOver) throw new Error('startNextHand: previous hand not finished');
  if (state.matchOver) throw new Error('startNextHand: match is over');
  const deal = dealHands(state.ruleSet, state.playerCount, seed);
  const leader = state.ruleSet.nextHandLeader === 'rotate'
    ? (state.leaderSeat + 1) % state.playerCount
    : winnerLeader(state);
  return {
    ...state,
    handNumber: state.handNumber + 1,
    seed,
    hands: deal.hands,
    boneyard: deal.boneyard,
    chain: EMPTY_CHAIN,
    currentSeat: leader,
    leaderSeat: leader,
    pendingMuggins: null,
    handOver: false,
    consecutivePasses: 0,
    // Forced-lead is a first-hand-only rule, even in Телефон. The winner
    // of the previous hand leads with any tile they choose.
    forcedLeadTile: null,
    lastAction: null,
  };
}

/**
 * Convenience check: can the current player make any move (play or draw)?
 * Used by callers to decide between offering a draw, a pass, or auto-passing.
 */
export function canCurrentPlayerAct(state: GameStateData): boolean {
  const hand = state.hands[state.currentSeat];
  if (!hand) return false;
  if (hasLegalPlay(state.chain, hand, state.forcedLeadTile)) return true;
  if (state.boneyard.length > 0 && state.ruleSet.drawRule !== 'no-draw') return true;
  return false;
}
